import React, { useState, useEffect } from 'react';
import { ukWindowIso } from '../utils/ukTime';
import './Maintenance.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Backend refusal codes -> plain English. No raw codes reach the user.
const ERROR_MESSAGES = {
  no_tenant_overlap: "The tenant's stated availability doesn't cover this time.",
  slot_not_in_proposed_windows: "The contractor's proposed slots don't include this time.",
  atomic_book_error: "Something went wrong recording the booking. Try again in a moment.",
  race_lost: "Another update landed at the same time. Try again.",
  social_hours_violated: "This is outside working hours (08:00-18:00).",
};

export function translateManualBookError(raw) {
  if (!raw) return 'The booking could not be recorded.';
  return ERROR_MESSAGES[raw] || 'The booking could not be recorded. Try a different time.';
}

const TRADE_CATEGORIES = [
  'heating', 'electrical', 'plumbing', 'handyman', 'general',
];
const TRADE_LABEL = { heating: 'Gas & Heating (Gas Safe registered)' };

const _labelStyle = { display: 'block', fontSize: '.85em', marginBottom: '.25rem', color: '#333' };
const _inputStyle = { padding: '.4rem', width: '100%', boxSizing: 'border-box' };

/**
 * Reusable contractor picker + inline "create new contractor" block.
 * Used by ManualBookingModal AND MaintenanceForm's YES route.
 *
 * Owns its OWN contractors list state, inline-create panel state, and
 * new-contractor field state. Emits selected id via
 * `onContractorIdChange`. Emits create/api errors via `onError` so
 * the parent can render them wherever it renders its own errors.
 */
export function ContractorPickerBlock({
  apiKey, contractorId, onContractorIdChange, onError,
  disabled = false,
  testIdPrefix = 'mb',
}) {
  const [contractors, setContractors] = useState(null); // null = loading
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newContractor, setNewContractor] = useState({
    company_name: '', contact_name: '', phone_number: '',
    email: '', trade_category: 'general',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetch(`${API}/api/contractors`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      if (!res.ok) {
        if (!cancelled) setContractors([]);
        return;
      }
      const data = await res.json();
      if (!cancelled) setContractors(data.contractors || []);
    })();
    return () => { cancelled = true; };
  }, [apiKey]);

  const createContractorInline = async () => {
    onError && onError('');
    const c = newContractor;
    if (!c.company_name || !c.contact_name || !c.phone_number || !c.email) {
      onError && onError(
        'Company, contact name, phone and email are required to create a contractor.',
      );
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/contractors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
        body: JSON.stringify(c),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = Array.isArray(data.detail)
          ? (data.detail[0] && data.detail[0].msg) || 'Could not create contractor.'
          : (data.detail || 'Could not create contractor.');
        onError && onError(detail);
        return;
      }
      setContractors(prev => [...(prev || []), data]);
      onContractorIdChange(data.id);
      setShowCreate(false);
      setNewContractor({
        company_name: '', contact_name: '', phone_number: '',
        email: '', trade_category: 'general',
      });
    } catch (err) {
      onError && onError('Could not create contractor — please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {!showCreate && (
        <div style={{ display: 'grid', gap: '.5rem', marginBottom: '1rem', padding: '.75rem', background: '#f7f8fa', border: '1px solid #e2e6ee', borderRadius: 6 }}>
          <label style={{ display: 'block' }}>
            <span style={_labelStyle}>Contractor</span>
            <select
              value={contractorId}
              onChange={e => onContractorIdChange(e.target.value)}
              data-testid={`${testIdPrefix}-contractor-picker`}
              disabled={disabled || contractors === null}
              style={_inputStyle}
            >
              <option value="">
                {contractors === null ? 'Loading contractors...'
                  : contractors.length === 0 ? 'No contractors yet — create one below'
                  : 'Select a contractor'}
              </option>
              {(contractors || []).map(c => (
                <option key={c.id} value={c.id}>
                  {c.company_name} — {c.contact_name} ({c.trade_category})
                </option>
              ))}
            </select>
          </label>
          <button
            className="md-btn"
            onClick={() => setShowCreate(true)}
            data-testid={`${testIdPrefix}-open-create-contractor`}
            style={{ fontSize: '.85em', padding: '.35rem .6rem', alignSelf: 'flex-start' }}
            disabled={disabled}
          >
            + Create new contractor
          </button>
        </div>
      )}
      {showCreate && (
        <div style={{ display: 'grid', gap: '.5rem', marginBottom: '1rem', padding: '.75rem', background: '#eef6ff', border: '1px solid #b6d5f2', borderRadius: 6 }}>
          <div style={{ fontSize: '.85em', fontWeight: 600, marginBottom: '.25rem' }}>New contractor</div>
          <label><span style={_labelStyle}>Company name</span>
            <input type="text" value={newContractor.company_name}
                   onChange={e => setNewContractor(n => ({ ...n, company_name: e.target.value }))}
                   data-testid={`${testIdPrefix}-new-company`} style={_inputStyle} />
          </label>
          <label><span style={_labelStyle}>Contact name</span>
            <input type="text" value={newContractor.contact_name}
                   onChange={e => setNewContractor(n => ({ ...n, contact_name: e.target.value }))}
                   data-testid={`${testIdPrefix}-new-contact`} style={_inputStyle} />
          </label>
          <label><span style={_labelStyle}>Phone (E.164, e.g. +447911123456)</span>
            <input type="text" value={newContractor.phone_number}
                   onChange={e => setNewContractor(n => ({ ...n, phone_number: e.target.value }))}
                   data-testid={`${testIdPrefix}-new-phone`} style={_inputStyle} />
          </label>
          <label><span style={_labelStyle}>Email</span>
            <input type="email" value={newContractor.email}
                   onChange={e => setNewContractor(n => ({ ...n, email: e.target.value }))}
                   data-testid={`${testIdPrefix}-new-email`} style={_inputStyle} />
          </label>
          <label><span style={_labelStyle}>Trade</span>
            <select value={newContractor.trade_category}
                    onChange={e => setNewContractor(n => ({ ...n, trade_category: e.target.value }))}
                    data-testid={`${testIdPrefix}-new-trade`} style={_inputStyle}>
              {TRADE_CATEGORIES.map(t => <option key={t} value={t}>{TRADE_LABEL[t] || t}</option>)}
            </select>
          </label>
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.5rem' }}>
            <button className="md-btn md-btn-primary" onClick={createContractorInline}
                    disabled={creating} data-testid={`${testIdPrefix}-create-contractor-submit`}>
              {creating ? 'Creating...' : 'Save contractor'}
            </button>
            <button className="md-btn" onClick={() => { setShowCreate(false); onError && onError(''); }}
                    data-testid={`${testIdPrefix}-create-contractor-cancel`}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Reusable date / start-time / duration triple. Used by
 *  ManualBookingModal and MaintenanceForm YES route. */
export function DateStartDurationBlock({
  date, setDate, startTime, setStartTime, duration, setDuration,
  testIdPrefix = 'mb',
  disabled = false,
}) {
  return (
    <div style={{ display: 'grid', gap: '.75rem' }}>
      <label style={{ display: 'block' }}>
        <span style={_labelStyle}>Date</span>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
               data-testid={`${testIdPrefix}-date`} style={_inputStyle} disabled={disabled} />
      </label>
      <label style={{ display: 'block' }}>
        <span style={_labelStyle}>Start time</span>
        <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
               data-testid={`${testIdPrefix}-start`} style={_inputStyle} disabled={disabled} />
      </label>
      <label style={{ display: 'block' }}>
        <span style={_labelStyle}>Duration</span>
        <select value={duration} onChange={e => setDuration(parseInt(e.target.value, 10))}
                data-testid={`${testIdPrefix}-duration`} style={_inputStyle} disabled={disabled}>
          <option value={30}>30 minutes</option>
          <option value={45}>45 minutes</option>
          <option value={60}>1 hour</option>
          <option value={90}>1½ hours</option>
          <option value={120}>2 hours</option>
        </select>
      </label>
    </div>
  );
}

/**
 * Manual booking modal — self-sufficient. If no outreach exists,
 * the picker + inline create appear; backend synthesises the outreach
 * row and books through the shared choke. Accepts optional
 * `initialValues` so callers (e.g. MaintenanceForm after a YES-route
 * failure or override-cancel) can prime it with a slot the operator
 * already chose.
 */
export default function ManualBookingModal({
  mrId, outreachId, apiKey, onClose, onBooked,
  initialValues,
}) {
  const iv = initialValues || {};
  const [date, setDate] = useState(iv.date || '');
  const [startTime, setStartTime] = useState(iv.startTime || '10:00');
  const [duration, setDuration] = useState(iv.duration || 60);
  const [contractorId, setContractorId] = useState(iv.contractorId || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(iv.priorErrorMessage || '');
  const [override, setOverride] = useState(null);

  const needsContractor = !outreachId;

  const submit = async ({ allowSocial = false, allowPast = false } = {}) => {
    setError('');
    if (needsContractor && !contractorId) {
      setError('Pick a contractor (or create one) before booking.');
      return;
    }
    if (!date) { setError('Pick a date first.'); return; }
    setBusy(true);
    const win = ukWindowIso(date, startTime, duration);
    try {
      const body = {
        scheduled_start: win.start,
        scheduled_end: win.end,
        allow_outside_social_hours: allowSocial,
        allow_past_time: allowPast,
      };
      if (outreachId) body.outreach_id = outreachId;
      if (needsContractor && contractorId) body.contractor_id = contractorId;

      const res = await fetch(`${API}/api/maintenance/${mrId}/manual-book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.override_available) { setOverride(data); return; }
      if (!res.ok) { setError(translateManualBookError(data.detail || data.reason)); return; }
      onBooked && onBooked(data);
      onClose();
    } catch (err) {
      setError('The booking could not be recorded — please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (override) {
    const heading = override.kind === 'social_hours'
      ? 'Outside working hours'
      : 'Time in the past';
    return (
      <div className="modal-backdrop" data-testid="manual-booking-modal"
           style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
        <div style={{ background: '#fff', padding: '1.5rem', borderRadius: 8, minWidth: 400, maxWidth: 480 }}>
          <h3 style={{ marginTop: 0, color: '#b57000' }} data-testid="mb-override-heading">{heading}</h3>
          <p data-testid="mb-override-message" style={{ fontSize: '.95em', lineHeight: 1.5 }}>
            {override.kind === 'social_hours'
              ? 'This is outside working hours (08:00-18:00). Both the contractor and the tenant will be sent a confirmation for this time.'
              : 'This time has already passed. Both the contractor and the tenant will be sent a confirmation for it.'}
          </p>
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '1.5rem' }}>
            <button
              className="md-btn md-btn-primary"
              onClick={() => submit({
                allowSocial: override.kind === 'social_hours',
                allowPast: override.kind === 'past_time',
              })}
              disabled={busy}
              data-testid="mb-confirm-override"
            >
              {busy ? 'Booking...' : 'Book anyway'}
            </button>
            <button
              className="md-btn"
              onClick={() => { setOverride(null); setError(''); }}
              data-testid="mb-override-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" data-testid="manual-booking-modal"
         style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
      <div style={{ background: '#fff', padding: '1.5rem', borderRadius: 8, minWidth: 440, maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>Set booking manually</h3>
        {iv.priorErrorMessage ? (
          <p style={{ color: '#b57000', fontSize: '.9em', marginBottom: '1rem',
                       padding: '.5rem .75rem', background: '#fff7e6',
                       border: '1px solid #f0c883', borderRadius: 6 }}
             data-testid="mb-primed-message">
            {iv.priorErrorMessage}
          </p>
        ) : (
          <p style={{ color: '#666', fontSize: '.9em', marginBottom: '1rem' }}>
            Records a booking you agreed off-system. The tenant and contractor will both receive the standard confirmation with a calendar invite.
          </p>
        )}
        {needsContractor && (
          <ContractorPickerBlock
            apiKey={apiKey}
            contractorId={contractorId}
            onContractorIdChange={setContractorId}
            onError={setError}
            testIdPrefix="mb"
          />
        )}
        <DateStartDurationBlock
          date={date} setDate={setDate}
          startTime={startTime} setStartTime={setStartTime}
          duration={duration} setDuration={setDuration}
          testIdPrefix="mb"
        />
        {error && (
          <div data-testid="mb-error" style={{ color: '#c00', margin: '.75rem 0', fontSize: '.9em' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: '.5rem', marginTop: '1.25rem' }}>
          <button
            className="md-btn md-btn-primary"
            onClick={() => submit()}
            disabled={busy || (needsContractor && !contractorId)}
            data-testid="mb-submit"
          >
            {busy ? 'Booking...' : 'Book'}
          </button>
          <button
            className="md-btn"
            onClick={onClose}
            data-testid="mb-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
